/**
 * Reminder engine: default resolution, fire-time materialisation, nag scheduling and
 * snooze targets. The SQL triggers and the `send-notifications` Edge Function implement
 * the same rules; tests here are the reference.
 */
import {
  addDays,
  localDateTimeToInstant,
  normalizeTime,
  todayInTz,
  toLocalParts,
  toLocalTime,
} from './time.ts';
import type {
  ISOTime,
  Instant,
  Profile,
  Reminder,
  ReminderChannels,
  ReminderDefault,
  ReminderSource,
  ReminderState,
} from './types.ts';

/** 1 day before + at due, used when neither the list nor the profile defines defaults. */
export const BUILTIN_DEFAULT_REMINDERS: readonly ReminderDefault[] = [
  { offset_minutes: -1440 },
  { offset_minutes: 0 },
];

export const DEFAULT_CHANNELS: ReminderChannels = { push: true, email: true };

/** Relative presets offered in the reminder editor, in minutes relative to due. */
export const RELATIVE_PRESETS: readonly { label: string; offset_minutes: number }[] = [
  { label: '1 week before', offset_minutes: -10080 },
  { label: '1 day before', offset_minutes: -1440 },
  { label: '1 hour before', offset_minutes: -60 },
  { label: '10 minutes before', offset_minutes: -10 },
  { label: 'At due time', offset_minutes: 0 },
  { label: '1 day after', offset_minutes: 1440 },
];

export interface ResolvedDefaults {
  defaults: ReminderDefault[];
  source: Extract<ReminderSource, 'list_default' | 'global_default'>;
}

/**
 * Defaults for a new task: the list's own set, else the profile's, else the built-in pair.
 * An empty array is a valid choice ("no default reminders").
 */
export function resolveDefaultReminders(
  list: { default_reminders: ReminderDefault[] | null } | null | undefined,
  profile: { default_reminders: ReminderDefault[] | null } | null | undefined,
): ResolvedDefaults {
  if (list?.default_reminders)
    return { defaults: [...list.default_reminders], source: 'list_default' };
  if (profile?.default_reminders)
    return { defaults: [...profile.default_reminders], source: 'global_default' };
  return { defaults: [...BUILTIN_DEFAULT_REMINDERS], source: 'global_default' };
}

/** The time of day date-only tasks are "due" for reminder purposes. */
export function dateOnlyReminderTime(
  profile: Pick<Profile, 'date_only_reminder_time' | 'day_start_time'>,
): ISOTime {
  return normalizeTime(profile.date_only_reminder_time ?? profile.day_start_time);
}

export function dailySummaryTime(
  profile: Pick<Profile, 'daily_summary_time' | 'day_start_time'>,
): ISOTime {
  return normalizeTime(profile.daily_summary_time ?? profile.day_start_time);
}

/** When a reminder should fire, or null when it cannot (relative reminder on an undated task). */
export function materialiseFireAt(
  reminder: Pick<Reminder, 'kind' | 'at' | 'offset_minutes'>,
  dueAt: Instant | null,
): Instant | null {
  if (reminder.kind === 'absolute') return reminder.at;
  if (!dueAt || reminder.offset_minutes === null) return null;
  return new Date(Date.parse(dueAt) + reminder.offset_minutes * 60_000).toISOString();
}

/** Grace period: a reminder materialised up to this far in the past still fires. */
export const PAST_GRACE_MS = 2 * 60_000;

/**
 * State for a reminder whose fire time was just (re)computed. Reminders that land in the
 * past are skipped instead of firing a burst of stale notifications.
 */
export function stateForFireAt(fireAt: Instant | null, now: Date): ReminderState {
  if (!fireAt) return 'cancelled';
  return Date.parse(fireAt) >= now.getTime() - PAST_GRACE_MS ? 'scheduled' : 'cancelled';
}

/** The instant the cron job compares against `now()`. */
export function effectiveFireAt(reminder: Pick<Reminder, 'fire_at' | 'snoozed_until'>): Instant {
  return reminder.snoozed_until ?? reminder.fire_at;
}

export function isDue(
  reminder: Pick<Reminder, 'fire_at' | 'snoozed_until' | 'state' | 'deleted_at'>,
  now: Date,
): boolean {
  if (reminder.deleted_at) return false;
  if (reminder.state !== 'scheduled' && reminder.state !== 'snoozed') return false;
  return Date.parse(effectiveFireAt(reminder)) <= now.getTime();
}

export interface AfterFire {
  state: ReminderState;
  fire_at: Instant;
  /** Sends so far, including the first one. */
  nag_sent: number;
  snoozed_until: null;
  /** Kind to log for the send that just happened. */
  sentKind: 'reminder' | 'nag';
}

/** Kind to log for the send about to happen: the first send (or a snoozed one) is a reminder. */
export function sendKind(
  reminder: Pick<Reminder, 'nag_sent' | 'snoozed_until'>,
): 'reminder' | 'nag' {
  return reminder.nag_sent > 0 && !reminder.snoozed_until ? 'nag' : 'reminder';
}

/**
 * What a reminder becomes after it was sent. `nag_sent` counts every send including the
 * first. Nagging repeats every `nag_every_minutes` until the task is complete (the caller
 * stops when it is) or `nag_count` repeats were sent after the first reminder.
 */
export function afterFire(
  reminder: Pick<
    Reminder,
    'fire_at' | 'snoozed_until' | 'nag_every_minutes' | 'nag_until' | 'nag_count' | 'nag_sent'
  >,
  now: Date,
): AfterFire {
  const sentKind = sendKind(reminder);
  const nagSent = reminder.nag_sent + 1;
  const every = reminder.nag_every_minutes;
  const keepNagging =
    !!every &&
    every > 0 &&
    (reminder.nag_until === 'task_complete' ||
      (reminder.nag_until === 'count' && nagSent - 1 < (reminder.nag_count ?? 0)));
  if (keepNagging) {
    const base = Date.parse(effectiveFireAt(reminder));
    let next = base + every * 60_000;
    if (next <= now.getTime()) next = now.getTime() + every * 60_000;
    return {
      state: 'scheduled',
      fire_at: new Date(next).toISOString(),
      nag_sent: nagSent,
      snoozed_until: null,
      sentKind,
    };
  }
  return {
    state: 'fired',
    fire_at: reminder.fire_at,
    nag_sent: nagSent,
    snoozed_until: null,
    sentKind,
  };
}

export type SnoozeOption = '10m' | '1h' | 'tomorrow' | { until: Instant };

export const SNOOZE_LABEL: Record<Exclude<SnoozeOption, { until: Instant }>, string> = {
  '10m': '10 minutes',
  '1h': '1 hour',
  tomorrow: 'Tomorrow morning',
};

/** Instant a snoozed reminder fires again. "Tomorrow morning" uses "My day starts at". */
export function snoozeTarget(
  option: SnoozeOption,
  now: Date,
  tz: string,
  dayStartTime: ISOTime,
): Date {
  if (typeof option === 'object') return new Date(option.until);
  switch (option) {
    case '10m':
      return new Date(now.getTime() + 10 * 60_000);
    case '1h':
      return new Date(now.getTime() + 60 * 60_000);
    case 'tomorrow':
      return localDateTimeToInstant(addDays(todayInTz(tz, now), 1), dayStartTime, tz);
  }
}

/** "1 day before", "At due time", "2 hours after", "1 week before". */
export function formatOffset(offsetMinutes: number): string {
  if (offsetMinutes === 0) return 'At due time';
  const abs = Math.abs(offsetMinutes);
  const suffix = offsetMinutes < 0 ? 'before' : 'after';
  const units: [number, string][] = [
    [10080, 'week'],
    [1440, 'day'],
    [60, 'hour'],
    [1, 'minute'],
  ];
  for (const [size, name] of units) {
    if (abs % size === 0) {
      const n = abs / size;
      return `${n} ${name}${n === 1 ? '' : 's'} ${suffix}`;
    }
  }
  /* c8 ignore next */
  return `${abs} minutes ${suffix}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * "Thu 1 Oct, 10:30" in the user's zone. Uses fixed abbreviations rather than ICU locale
 * data, which varies by runtime (en-GB renders September as "Sept" in newer ICU builds).
 */
export function formatFireAt(fireAt: Instant, tz: string): string {
  const p = toLocalParts(fireAt, tz);
  return `${WEEKDAYS[p.weekday]} ${p.day} ${MONTHS[p.month - 1]}, ${toLocalTime(fireAt, tz)}`;
}

export function formatChannels(channels: ReminderChannels): string {
  const parts = [channels.push ? 'push' : null, channels.email ? 'email' : null].filter(Boolean);
  return parts.length ? parts.join(' + ') : 'in-app only';
}

/** Payload rows for default reminders on a task that just got a due date. */
export function buildDefaultReminderRows(
  defaults: readonly ReminderDefault[],
  source: ReminderSource,
  dueAt: Instant,
  now: Date,
): Pick<
  Reminder,
  'kind' | 'at' | 'offset_minutes' | 'fire_at' | 'channels' | 'state' | 'source' | 'nag_sent'
>[] {
  return defaults.map((d) => {
    const fireAt = materialiseFireAt(
      { kind: 'relative', at: null, offset_minutes: d.offset_minutes },
      dueAt,
    )!;
    return {
      kind: 'relative',
      at: null,
      offset_minutes: d.offset_minutes,
      fire_at: fireAt,
      channels: d.channels ?? { ...DEFAULT_CHANNELS },
      state: stateForFireAt(fireAt, now),
      source,
      nag_sent: 0,
    };
  });
}
